import { CloseCircle } from 'iconsax-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Facebook from '@/assets/socialMedia/Facebook.svg?react'
import Instagram from '@/assets/socialMedia/Instagram.svg?react'
import LinkedIn from '@/assets/socialMedia/LinkedIn.svg?react'
import Pinterest from '@/assets/socialMedia/Pinterest.svg?react'
import Reddit from '@/assets/socialMedia/Reddit.svg?react'
import TikTok from '@/assets/socialMedia/TikTok.svg?react'
import X from '@/assets/socialMedia/X.svg?react'
import { Divider } from '@/components/Divider/Divider.component'
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@/components/ResponsiveModal/ResponsiveModal'
import { Button } from '@/components/ui/button'
import { useCheckProfilesPrivacyMutation } from '@/store/api/NewSearch/NewSearch.query'
import {
  ESocialMedia,
  type privacyResult,
  type profilePrivacyDto,
} from '@/store/api/NewSearch/NewSearch.types'
import type { TSocialMediaUser } from '@/store/api/Users/Users.types'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

const socialMediaIcons: Record<
  string,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  [ESocialMedia.FACEBOOK]: Facebook,
  [ESocialMedia.INSTAGRAM]: Instagram,
  [ESocialMedia.LINKEDIN]: LinkedIn,
  [ESocialMedia.PINTEREST]: Pinterest,
  [ESocialMedia.REDDIT]: Reddit,
  [ESocialMedia.TIKTOK]: TikTok,
  [ESocialMedia.TWITTER]: X,
}

type PopupState = 'pre-check' | 'loading' | 'results'

interface CheckPrivacityPopUpProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  socialMedias: TSocialMediaUser[]
  onRunReport: () => void
}

export const CheckPrivacityPopUp = ({
  open,
  onOpenChange,
  socialMedias,
  onRunReport,
}: CheckPrivacityPopUpProps) => {
  const { t } = useTranslation()
  const [state, setState] = useState<PopupState>('pre-check')
  const [privacyResults, setPrivacyResults] = useState<privacyResult[]>([])

  const [checkPrivacy] = useCheckProfilesPrivacyMutation()

  const validSocialMedias = useMemo(
    () =>
      socialMedias.filter(
        (sm) => sm.profileLink && sm.profileLink.trim() !== '',
      ),
    [socialMedias],
  )

  const allPublic = useMemo(
    () =>
      privacyResults.length > 0 && privacyResults.every((r) => !r.isPrivate),
    [privacyResults],
  )

  const privateCount = useMemo(
    () => privacyResults.filter((r) => r.isPrivate).length,
    [privacyResults],
  )

  const handleCheckPrivacy = async () => {
    setState('loading')

    const profiles: profilePrivacyDto[] = validSocialMedias.map((sm) => ({
      socialMedia: sm.name as ESocialMedia,
      url: sm.profileLink,
    }))

    try {
      const response = await checkPrivacy({ profiles }).unwrap()
      setPrivacyResults(response.info.data.results)
      setState('results')
    } catch (error) {
      setState('results')
      setPrivacyResults([])
    }
  }

  const handleRecheck = () => {
    setPrivacyResults([])
    handleCheckPrivacy()
  }

  const handleCancel = () => {
    setState('pre-check')
    setPrivacyResults([])
    onOpenChange(false)
  }

  const handleRunReport = () => {
    onOpenChange(false)
    onRunReport()
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setState('pre-check')
      setPrivacyResults([])
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalContent
        className="md:max-w-2xl bg-baseColor p-6 max-md:p-4 flex flex-col gap-4 max-md:gap-3"
        side="bottom"
      >
        <ResponsiveModalClose asChild>
          <button
            onClick={handleCancel}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          >
            <CloseCircle size={24} color={SocialMirrorPalette.neutralColor80} />
          </button>
        </ResponsiveModalClose>

        <ResponsiveModalHeader>
          {state === 'results' ? (
            <>
              <ResponsiveModalDescription className="text-neutralColor text-2xl font-medium">
                {allPublic
                  ? t('search.privacyCheck.allPublicDescription')
                  : t('search.privacyCheck.resultsDescription', {
                      count: privateCount,
                    })}
              </ResponsiveModalDescription>
              <ResponsiveModalTitle className="text-neutralColor80 text-base font-normal">
                {t('search.privacyCheck.resultsTitle')}
              </ResponsiveModalTitle>
            </>
          ) : (
            <>
              <ResponsiveModalTitle className="text-neutralColor text-2xl max-md:text-xl font-medium">
                {t('search.privacyCheck.title')}
              </ResponsiveModalTitle>
              <ResponsiveModalDescription className="text-neutralColor80 text-base max-md:text-sm font-normal">
                {t('search.privacyCheck.description')}
              </ResponsiveModalDescription>
            </>
          )}
        </ResponsiveModalHeader>

        <Divider />

        {/* Social Media List */}
        <div className="flex flex-col gap-3 max-h-[300px] max-md:max-h-none max-md:flex-1 md:overflow-y-auto">
          {state === 'results' && privacyResults.length > 0
            ? privacyResults.map((result, index) => {
                const IconComponent = socialMediaIcons[result.socialMedia] || X
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-xl bg-baseColorHighlight"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primaryColor30 border border-primary30">
                        <IconComponent
                          width={20}
                          height={20}
                          color={SocialMirrorPalette.primary}
                        />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-neutralColor text-sm font-medium">
                          {result.socialMedia}
                        </span>
                        <span className="text-neutralColor80 text-xs truncate">
                          {result.url}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`text-sm font-medium px-3 py-1 rounded-full ${
                        result.isPrivate
                          ? 'bg-alertRedBase text-alertRedSecondary'
                          : 'bg-alertGreenBase text-alertGreenSecondary'
                      }`}
                    >
                      {result.isPrivate
                        ? t('search.privacyCheck.private')
                        : t('search.privacyCheck.public')}
                    </span>
                  </div>
                )
              })
            : validSocialMedias.map((sm, index) => {
                const IconComponent = socialMediaIcons[sm.name] || X
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl bg-baseColorHighlight"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primaryColor30 border border-primary30">
                      <IconComponent
                        width={20}
                        height={20}
                        color={SocialMirrorPalette.primary}
                      />
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-neutralColor text-sm font-medium">
                        {sm.name}
                      </span>
                      <span className="text-neutralColor80 text-xs truncate">
                        {sm.profileLink}
                      </span>
                    </div>
                  </div>
                )
              })}
        </div>

        {state === 'results' && !allPublic && privateCount > 0 && (
          <>
            <Divider />
            <div className="p-3 rounded-xl border bg-alertYellowBase border-alertYellowSecondary">
              <p className="text-sm text-alertYellowSecondary">
                {t('search.privacyCheck.warningMessage')}
              </p>
            </div>
          </>
        )}

        <Divider />

        <div className="flex justify-end items-center">
          <div className="flex gap-3 max-md:flex-col max-md:w-full">
            {state === 'pre-check' && (
              <Button
                onClick={handleCheckPrivacy}
                className="h-[46px] px-5 py-3 rounded-3xl max-md:w-full"
              >
                {t('search.privacyCheck.verifyButton')}
              </Button>
            )}

            {state === 'loading' && (
              <Button
                disabled
                isLoading
                className="h-[46px] px-5 py-3 rounded-3xl max-md:w-full"
              >
                {t('search.privacyCheck.checking')}
              </Button>
            )}

            {state === 'results' && (
              <>
                <Button
                  variant={allPublic ? 'default' : 'outline'}
                  onClick={handleRunReport}
                  className="h-[46px] px-5 py-3 rounded-3xl max-md:w-full max-md:order-2"
                >
                  {t('search.privacyCheck.runReportButton')}
                </Button>
                {!allPublic && (
                  <Button
                    onClick={handleRecheck}
                    className="h-[46px] px-5 py-3 rounded-3xl max-md:w-full max-md:order-1"
                    style={{ backgroundColor: SocialMirrorPalette.primary }}
                  >
                    {t('search.privacyCheck.recheckButton')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
